import { useAtomValue } from "jotai";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { inputClass } from "@/lib/forms";
import { validatePhotoFile } from "@/modules/plus/photos/plus.photos.api";
import { atoms } from "@/state";

// Shape both galleries (template-driven + basic-mode) render. Decoupled from
// the schemas so basic-mode can produce items without a ModelTemplate.
export interface GalleryItem {
  url: string;
  code: string;
  meaning?: string;
  source: "baseline" | "uploaded";
  uploadedBy?: string;
  // Storage path — present only for user uploads. Required for deletion.
  imagePath?: string;
  // Identifier used by callers to dedupe/group; not displayed.
  kind: string;
}

interface UploadCardProps {
  // The starting value for the code input. Pass the composed code in
  // template-mode, or just the model code in basic-mode.
  defaultCode: string;
  helpText?: string;
}

// Re-prefills from `defaultCode` until the user has typed in the field
// themselves — then we stop tracking so manual edits aren't clobbered.
export const UploadCard: React.FC<UploadCardProps> = ({ defaultCode, helpText }) => {
  const [code, setCode] = useState(defaultCode);
  const [pristine, setPristine] = useState(true);
  const uploadMutation = useAtomValue(atoms.photos.upload);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const fileInput = useRef<HTMLInputElement>(null);

  if (pristine && code !== defaultCode) {
    setCode(defaultCode);
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error("Enter a code before uploading");
      return;
    }
    const validationError = validatePhotoFile(file);
    if (validationError) {
      toast.error(validationError);
      if (fileInput.current) {
        fileInput.current.value = "";
      }
      return;
    }
    try {
      await uploadMutation.mutateAsync({ code: trimmed, file });
      toast.success(`Photo added for ${trimmed}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      if (fileInput.current) {
        fileInput.current.value = "";
      }
    }
  };

  if (!signedIn) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50/40 px-3 py-3 dark:border-blue-800 dark:bg-blue-950/20">
      <label
        className="text-xs font-medium text-gray-700 dark:text-gray-300"
        htmlFor="gallery-upload-code"
      >
        Photo code
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <input
          id="gallery-upload-code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setPristine(false);
          }}
          placeholder="e.g. 6500-O-9x9-24x24-L-AW-4A-SR08"
          className={`${inputClass} min-w-[18rem] flex-1 font-mono`}
        />
        <button
          type="button"
          disabled={uploadMutation.isPending || !code.trim()}
          onClick={() => fileInput.current?.click()}
          className="rounded-md border border-blue-500 bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {uploadMutation.isPending ? "Uploading…" : "Upload photo"}
        </button>
      </div>
      {helpText && <p className="text-[11px] text-gray-500 dark:text-gray-400">{helpText}</p>}
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={onFile}
        className="hidden"
      />
    </div>
  );
};

interface ThumbProps {
  item: GalleryItem;
  signedIn: boolean;
  busy: boolean;
  onOpen: () => void;
  onRemove: () => void;
}

export const Thumb: React.FC<ThumbProps> = ({ item, signedIn, busy, onOpen, onRemove }) => (
  <figure className="relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
    {signedIn && item.source === "uploaded" && item.imagePath && (
      <button
        type="button"
        disabled={busy}
        onClick={onRemove}
        title="Remove this photo"
        aria-label="Remove this photo"
        className="absolute right-1 top-1 z-10 grid h-6 w-6 place-items-center rounded-full bg-white text-sm leading-none text-red-600 shadow ring-1 ring-red-300 hover:bg-red-50 disabled:opacity-50"
      >
        ×
      </button>
    )}
    <button
      type="button"
      onClick={onOpen}
      className="relative aspect-square overflow-hidden bg-gray-50 transition-transform hover:scale-[1.02] dark:bg-gray-950"
      title={item.source === "uploaded" ? `Uploaded by ${item.uploadedBy}` : "Baseline"}
    >
      <img src={item.url} alt={item.code} className="h-full w-full object-contain" loading="lazy" />
      <span className="absolute bottom-1 left-1 right-1 truncate rounded bg-black/70 px-1.5 py-0.5 text-center font-mono text-[11px] text-white">
        {item.code}
      </span>
    </button>
    {item.meaning && (
      <figcaption className="border-t border-gray-100 px-2 py-1 text-xs text-gray-600 dark:border-gray-800 dark:text-gray-400">
        {item.meaning}
      </figcaption>
    )}
  </figure>
);
